import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/useUser.store';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/shadcn-ui/button';
import CancelSubscriptionModal from '@/components/sharedComponents/CancelSubscriptionModal';

// ─── Sub-components ────────────────────────────────────────────────────────────

const ProgressBar = ({ value, max }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex-1 h-3 bg-[#3a3a3a] rounded-[20px] overflow-hidden">
      <div
        className="h-full bg-[#00579B] rounded-full transition-all duration-500"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

const InfoRow = ({ label, value, className }) => (
  <div className={cn('flex flex-col md:flex-row md:justify-between items-center text-sm', className)}>
    <span>{label}</span>
    <span className="text-white text-right">{value ?? '—'}</span>
  </div>
);

const SectionCard = ({ title, children, className }) => (
  <div className={cn('bg-[#282828] rounded-[20px] p-5 shadow-[0px_4px_4px_2px_#0000004D]', className)}>
    <h2 className="text-sm font-semibold text-white mb-4 font-baloo-2 border-b border-[#4E4E4E] py-2">{title}</h2>
    {children}
  </div>
);

const LoadingScreen = () => (
  <div className="dark flex items-center justify-center min-h-full w-full bg-[#1a1a1a]">
    <div className="w-7 h-7 border-2 border-[#00579B] border-t-transparent rounded-full animate-spin" />
  </div>
);

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Formats an ISO date string to "Month Day, Year". */
const formatDate = iso => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

/** Converts "pro_1" → "Pro 1", "monthly" → "Monthly", etc. */
const formatLabel = value => {
  if (!value) return '—';
  return value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

// ─── Page ──────────────────────────────────────────────────────────────────────

const AccountSettingsPage = () => {
  const user = useUserStore(state => state.user);
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openManageSubscriptionModal, setOpenManageSubscriptionModal] = useState(false);

  const fetchAccountDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await appAxios.get('/ProtectedPermitChat/v1.0/account-details');
      setData(res.data?.data ?? null);
    } catch (err) {
      toast.error('Failed to load account settings.', {
        description: err.response?.data?.message || err.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  if (isLoading) return <LoadingScreen />;

  const { accountDetails, subscription, credits } = data ?? {};

  const creditRemaining = credits?.remaining ?? 0;
  const creditUsed = credits?.used ?? 0;
  const creditTotal = credits?.total ?? 0;

  return (
    <div className="dark flex flex-col min-h-full w-full overflow-y-auto no-scrollbar font-baloo-2-thin bg-transparent text-white">
      {/* Page Header */}
      <div className="py-8 px-4 text-center">
        <h1 className="text-4xl mb-1.5">Account Settings</h1>
        <p className="text-xl mx-auto leading-relaxed">
          Manage your subscription, billing, payment details, and account usage.
        </p>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 gap-8 px-4 pb-4 w-full max-w-5xl mx-auto space-y-2">
        {/* ── Account Information ── */}
        <SectionCard title="Account Information">
          <div className="space-y-3">
            <InfoRow label="Full Name:" value={accountDetails?.fullname ?? user?.name} />
            <InfoRow label="Email Address:" value={accountDetails?.email ?? user?.email} />
            <InfoRow label="Company Name:" value={accountDetails?.companyName} />
          </div>
        </SectionCard>

        {/* ── Subscription ── */}
        <SectionCard title="Subscription">
          <div className="space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span>Subscription Plan:</span>
              <div className="flex items-center gap-2">
                {subscription?.plan !== "N/A" && subscription?.status !== "canceled" ? (
                  <>
                    <span className="text-white">{formatLabel(subscription?.plan)}</span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#00579B]/20 text-[#4fa3e8] border border-[#00579B]/30 tracking-wide">
                      ✦ Active
                    </span>
                  </>
                ) : (
                  <span className="text-white">No subscription</span>
                )}
              </div>
            </div>
            <InfoRow label="Billing Cycle:" value={subscription?.status !== "canceled" ? formatLabel(subscription?.billing) : '—'} />
            <InfoRow label="Renewal Date:" value={(subscription?.status !== "canceled" && subscription?.renewal !== "N/A") ? formatDate(subscription?.renewal) : '—'} />
            <div className='flex gap-2 w-full'>

              {(subscription?.status !== "canceled" && !subscription?.cancel_at_period_end && subscription?.renewal !== "N/A" && subscription?.plan !== "N/A") ? (
                <Button
                  variant="outline"
                  onClick={() => setOpenManageSubscriptionModal(true)}
                  className="border-gray-600 bg-transparent text-white hover:bg-white/5 hover:text-white text-sm h-10 rounded-[20px] w-full cursor-not-allowed"
                >
                  Manage Subscription
                </Button>
              ) : (
                <Button
                  variant="outline"
                  disabled={true}
                  className="border-gray-600 bg-transparent text-white hover:bg-white/5 hover:text-white text-sm h-10 rounded-[20px] w-full cursor-not-allowed"
                >
                  Manage Subscription
                </Button>
              )}
              {(subscription?.status === "canceled" || subscription?.cancel_at_period_end) && (
                <Button
                  variant="outline"
                  disabled={true}
                  className="border-gray-600 bg-transparent text-white hover:bg-white/5 hover:text-white text-sm h-10 rounded-[20px] w-full cursor-not-allowed"
                >
                  Subscription Canceled
                </Button>
              )}
              <Button
                onClick={() => navigate('/citypermit/subscription')}
                className="bg-[#00579B] hover:bg-[#00579B]/85 text-white text-sm h-10 font-semibold rounded-[20px] w-full"
              >
                Upgrade Plan
              </Button>
            </div>
          </div>
          {/* <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/citypermit/subscription')}
              className="border-gray-600 bg-transparent text-white hover:bg-white/5 hover:text-white text-sm h-10"
            >
              Manage Subscription
            </Button>
            <Button
              onClick={() => navigate('/citypermit/subscription')}
              className="bg-[#00579B] hover:bg-[#00579B]/85 text-white text-sm h-10 font-semibold"
            >
              Upgrade Plan
            </Button>
          </div> */}
        </SectionCard>

        {/* ── Credit Usage ── */}
        <SectionCard title="Credit Usage">
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="w-44 shrink-0">Permit Credit Balance:</span>
              <ProgressBar value={creditRemaining} max={creditTotal} />
              <span className="w-6 text-right tabular-nums">{creditRemaining}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-44 shrink-0">Used This Cycle:</span>
              <ProgressBar value={creditUsed} max={creditTotal} />
              <span className="w-6 text-right tabular-nums">{creditUsed}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-44 shrink-0">Total Monthly Credits:</span>
              <ProgressBar value={creditTotal} max={creditTotal} />
              <span className="w-6 text-right tabular-nums">{creditTotal}</span>
            </div>
          </div>
        </SectionCard>

        {/* Footer */}
        <p className="text-center text-[11px] text-gray-500 pb-2 leading-relaxed">
          By messaging Zermit AI, you agree to our{' '}
          <a href="/terms" className="underline hover:text-gray-300 transition-colors">
            Terms
          </a>{' '}
          and have read our{' '}
          <a href="/terms" className="underline hover:text-gray-300 transition-colors">
            Privacy Policy
          </a>
          .
        </p>
      </div>
      <CancelSubscriptionModal
        isOpen={openManageSubscriptionModal}
        onClose={() => setOpenManageSubscriptionModal(false)}
        onCancelled={() => fetchAccountDetails()}
        planName={formatLabel(subscription?.plan)}
        renewDate={subscription?.renewal !== "N/A" ? formatDate(subscription?.renewal) : '—'}
      />
    </div>
  );
};

export default AccountSettingsPage;
